// ServiceBanner.js — global service-health banner, under the tab bar, on every tab.
//
// Driven by /api/health/services (backend-cached 60 s), polled on load and every
// 10 minutes. Severity is by impact, not by service: a service flagged
// impact 'core' that is down → red (cannot be dismissed); anything else → amber
// (dismissable for the session). Browser-side failures (e.g. the ElevenLabs key
// stored in this browser rejected by /api/voices/sync) are merged in as well.
import React, { useState, useEffect, useCallback } from 'react';
import './ServiceBanner.css';

const BACKEND_FALLBACK = 'https://timbo-production.up.railway.app';
const POLL_MS = 10 * 60 * 1000;
const DISMISS_KEY = 'timbo_banner_dismissed_v1';

function readDismissed() {
  try { return JSON.parse(sessionStorage.getItem(DISMISS_KEY) || '[]'); } catch { return []; }
}

function fmtSince(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d)) return null;
  const today = new Date();
  if (d.toDateString() === today.toDateString()) {
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function severityOf(s) {
  return s.status === 'error' && s.impact === 'core' ? 'red' : 'amber';
}

// Services that need Yann's attention: down or degraded, never "not configured".
export function issuesFrom(health) {
  const services = (health && health.services) || [];
  return services.filter(s => s.status === 'error' || s.status === 'warn');
}

export default function ServiceBanner({ apiBase, showServer = true, clientIssues = [], onOpenService, onIssues }) {
  const [health, setHealth] = useState(null);
  const [dismissed, setDismissed] = useState(readDismissed);

  const base = apiBase || BACKEND_FALLBACK;
  const load = useCallback(() => {
    fetch(`${base}/api/health/services`)
      .then(r => r.json())
      .then(d => { if (d && Array.isArray(d.services)) setHealth(d); })
      .catch(() => {});
  }, [base]);

  useEffect(() => {
    if (!showServer) return undefined;
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
  }, [load, showServer]);

  const serverIssues = showServer ? issuesFrom(health) : [];
  const serverKeys = new Set(serverIssues.map(s => s.key));
  const issues = [
    ...serverIssues,
    // A browser-side failure only adds information when the server check does not already report it.
    ...clientIssues.filter(c => !serverKeys.has(c.service)),
  ];

  // Worst severity across all issues (dismissed ones included) — drives the Settings tab dot.
  const worst = issues.length ? (issues.some(s => severityOf(s) === 'red') ? 'red' : 'amber') : null;
  useEffect(() => {
    if (onIssues) onIssues(worst);
  }, [worst, onIssues]);

  const dismiss = (id) => {
    const next = [...dismissed, id];
    setDismissed(next);
    try { sessionStorage.setItem(DISMISS_KEY, JSON.stringify(next)); } catch {}
  };

  const rows = issues
    .map(s => ({ s, sev: severityOf(s), id: `${s.key}|${s.since || ''}` }))
    .filter(r => r.sev === 'red' || !dismissed.includes(r.id))
    .sort((a, b) => (a.sev === b.sev ? 0 : a.sev === 'red' ? -1 : 1));

  if (!rows.length) return null;

  return (
    <div className="svc-banner-stack" role="status" aria-live="polite">
      {rows.map(({ s, sev, id }) => {
        const since = fmtSince(s.since);
        const verb = s.status === 'error' ? 'is down' : 'needs attention';
        return (
          <div key={id} className={`svc-banner svc-banner--${sev}`}>
            <span className="svc-banner-dot" />
            <span className="svc-banner-text">
              <strong>{s.label}</strong> {verb}{since ? ` since ${since}` : ''} — {s.blocks || s.role}.
              {s.check && <span className="svc-banner-check"> ({s.check})</span>}
            </span>
            {onOpenService && (
              <button className="svc-banner-link" onClick={() => onOpenService(s.service || s.key)}>
                Fix in Settings →
              </button>
            )}
            {sev === 'amber' && (
              <button className="svc-banner-close" onClick={() => dismiss(id)} aria-label="Dismiss for this session" title="Dismiss for this session">×</button>
            )}
          </div>
        );
      })}
    </div>
  );
}
